import { TaskDataType, tasksStore, TaskType } from "@/stores/tasks";
import { BBox, Bundle, getCenteredBBox, Logger, safeCall } from "@noli/core";
import { recoverVirtualNode, SafeOp, VirtualNodeInfo } from "./safeOp";
import { BoardStore } from "@noli/business";
import { MetaType } from "@/types/meta";
import { Events } from "./events";
import { cancelBatchTasks } from "@/modules/editor/request/generator-task";
import { userAssetsStore } from "@/stores/userAssets";
import { isAuthUser } from "./status";
import { postHDImages } from "@/modules/editor/request/hd";

const baseInterval = 1000;

export enum BatchTaskEvent {
  FINISH = "finish",
}

// export const pendingPollingTimer: string | number | NodeJS.Timeout | undefined | null = null;
export const batchTaskPollingPool = new Bundle<BatchTaskPolling>([]);

export type BatchTaskType = {
  key: TaskType["key"]; // serialNumber
  type: TaskType["type"];
  isKafka: TaskType["isKafka"];
  taskPack: TaskType["taskPack"];
  status: TaskType["status"];
  pending: TaskType["pending"];
  forceGuest?: boolean;
  batchId: string;
  taskId?: string; // 需要等待任务被推送后才会有
  serialNumber: string;
  executing?: boolean;
  virtualNodeInfo: VirtualNodeInfo;
};

export type BatchPollingResolveData = {
  batchId: string;
  total: number;
  successCount: number;
};
export class BatchTaskPolling {
  batchId: string;
  key: string; // batchId
  tasks: Bundle<BatchTaskType>;
  private _total: number;
  successTaskCount = 0;

  private timerId: any | null = null;
  private getStatusFailedCounter = 0;
  private getStatusFailedPatience = 5;
  private mainReason?: "unsafe" | "exception" | "timeout";

  postprocess?: (data: {
    cdn: string;
    bbox: BBox;
    virtualAlias?: string;
    callback: () => Promise<void>;
    response: TaskDataType;
  }) => void;
  resolve: (value: BatchPollingResolveData | PromiseLike<BatchPollingResolveData>) => void = () =>
    void 0;
  reject: (reason?: any) => void = () => void 0;

  constructor(props: { batchId: string; tasks: BatchTaskType[] }) {
    this.batchId = props.batchId;
    this.key = props.batchId;
    this.tasks = new Bundle(props.tasks);
    this._total = props.tasks.length;
    batchTaskPollingPool.push(this);
  }

  cancelLastCheck = () => {
    clearTimeout(this.timerId);
  };

  get total() {
    return this._total;
  }
  set total(count: number) {
    if (typeof this._total === "number") {
      return;
    } else this._total = count;
  }

  cancelTask(taskKey: string) {
    this.cancelLastCheck();
    this.tasks.remove(taskKey);
    tasksStore.removeTask(taskKey);
  }

  async cancelBatchTasks() {
    console.log("cancelBatchTasks", this.batchId);
    // 清掉 polling 定时任务
    this.cancelLastCheck();
    // 去掉 taskStore 里的 batchs 中的数据
    tasksStore.removeBatch(this.batchId);
    // 去掉 taskStore 里的 tasks 里对应的任务
    tasksStore.removeBatchTasks(this.tasks);
    // 去掉 tasks 所对应 virtualNode
    const _tasks = this.tasks.clone();
    _tasks.forEach(async (task) => {
      this.tasks.remove(task.key);
      await recoverVirtualNode(task.virtualNodeInfo);
    });
    // 去掉 batchTaskPollingPool里的批次
    batchTaskPollingPool.remove(this.batchId);
  }

  getCheckTaskKeys() {
    const checkTaskKeys = this.tasks
      .filter(
        (task) =>
          (task.executing && task.status !== "finished" && task.status !== "exception") ||
          !task.executing,
      )
      // .slice(0, 5)
      .map((task) => task.serialNumber);
    return checkTaskKeys;
  }

  startTasksExecuting(taskKeys: string[]) {
    taskKeys.forEach((taskKey) => {
      const task = this.tasks.get(taskKey);
      if (task) task.executing = true;
    });
  }

  endTasksExecuting(taskKeys: string[]) {
    taskKeys.forEach((taskKey) => {
      const task = this.tasks.get(taskKey);
      if (task) task.executing = false;
    });
  }

  cleanupTask = async (
    task: BatchTaskType,
    opt?: {
      noRecoverVirtualNode?: boolean;
    },
  ) => {
    if (tasksStore.tasks.has(task.key)) {
      tasksStore.removeTask(task.key);
    } else {
      Logger.error(`cannot find '${task.key}' in 'tasksStore', which is problematic`);
    }
    if (this.tasks.has(task.key)) {
      this.tasks.remove(task.key);
    } else {
      Logger.error(`cannot find '${task.key}' in 'taskPollingPool', which is problematic`);
    }

    if (!opt?.noRecoverVirtualNode) {
      recoverVirtualNode(task.virtualNodeInfo);
    }
  };

  refresh() {
    console.log("refresh");
    this.check();
  }

  check = async (): Promise<void> => {
    this.cancelLastCheck();
    const checkTaskKeys = this.getCheckTaskKeys();

    if (!checkTaskKeys.length) {
      return;
    }
    this.startTasksExecuting(checkTaskKeys);
    const response = await SafeOp.getBatchKafkaStatus(this.batchId, checkTaskKeys);
    const tasksStatus = response?.data;

    if (!tasksStatus) {
      Logger.warn("failed to get status, retrying...");
      this.getStatusFailedCounter += 1;
      this.mainReason = "timeout";
      if (this.getStatusFailedCounter >= this.getStatusFailedPatience) {
        this.endTasksExecuting(checkTaskKeys);
        // throw Error;
        this.cancelBatchTasks();
        this.reject(this.mainReason);
        return;
      }
      this.timerId = setTimeout(this.check, baseInterval);
      return;
    }

    for (const status of tasksStatus) {
      const { serial_number: taskKey, engine_data, wait_task, push_status } = status;
      const engineData: {
        status: TaskType["status"];
        pending: TaskType["pending"];
        data: TaskDataType;
      } | null = engine_data ? JSON.parse(engine_data) : null;
      const task = this.tasks.get(taskKey);
      if (!task) {
        continue;
      }
      // 任务 取消推送 / 推送失败 / 执行失败
      if (push_status === 1 || push_status == 3 || push_status == 5) {
        this.mainReason = "exception";
        this.endTasksExecuting([task.key]);
        task.status = "exception";
        console.error(`===== Task ${task.key} failed!`);
        tasksStore.updateTask({
          ...task,
        });
        // 在 polling 里清掉任务
        this.cleanupTask(task);
        continue;
      }

      // 等待推送
      if (push_status === 0 || !engineData) {
        task.pending = wait_task;
        tasksStore.updateTask({
          ...task,
        });
        continue;
      }

      // 推送成功之后
      Object.assign(task, engineData);
      tasksStore.updateTask({ ...task });

      if (task.status === "exception" || (task.status === "finished" && !engineData.data?.safe)) {
        this.mainReason = task.status === "exception" ? "exception" : "unsafe";
        this.endTasksExecuting([task.key]);
        console.error(
          `===== Task ${task.key} ${
            task.status === "exception" ? "has exception, failed!" : "the result is not safe"
          }`,
        );
        // 在 polling 里清掉任务
        this.cleanupTask(task);
        if (isAuthUser()) userAssetsStore.fetchHeartCoin();
      }

      if (task.status === "finished" && engineData.data?.safe && engineData.data?.cdn) {
        this.endTasksExecuting([task.key]);
        const callback = async () => {
          // todo?
        };

        const url = engineData.data?.cdn;

        // if (task.taskPack.data.highres_info) {
        //   // 调高清接口
        //   const result = await postHDImages({
        //     url,
        //     w: task.taskPack.data.w,
        //     h: task.taskPack.data.h,
        //     is_ai: true,
        //     model: task.taskPack.data.model,
        //     guidance_scale: task.taskPack.data.guidance_scale,
        //     num_steps: task.taskPack.data.num_steps,
        //     text: task.taskPack.data.text,
        //     negative_prompt: task.taskPack.data.negative_prompt,
        //     seed: task.taskPack.data.seed,
        //     use_circular: task.taskPack.data.use_circular,
        //     custom_embeddings: task.taskPack.data.custom_embeddings ?? [],
        //   }).catch((err) => {
        //     console.error("hd image err: ", err);
        //     return {
        //       code: err?.code ?? 1,
        //       data: {
        //         url: null,
        //       },
        //     };
        //   });

        //   if (result.code === 1053) {
        //     this.mainReason = "unsafe";
        //     this.cleanupTask(task);
        //     continue;
        //   }

        //   if (result.code !== 0) {
        //     this.mainReason = "exception";
        //     this.cleanupTask(task);
        //     continue;
        //   }

        //   if (result.data?.url) {
        //     url = result.data.url;
        //   }
        // }

        const responseWithMeta: TaskDataType = {
          ...engineData.data,
          meta: task.taskPack.data as MetaType["data"],
        };
        const { w, h } = task.taskPack.data?.wh || task.taskPack.data;
        let bbox = await recoverVirtualNode(task.virtualNodeInfo);

        if (!bbox) {
          console.error(`===== ${task.virtualNodeInfo.alias} VirtualNode Error! `);
          bbox = getCenteredBBox(w, h, BoardStore.board);
        }

        console.log(`===== Task ${task.key} Succeeded!`);
        await this.postprocess?.({
          cdn: url,
          bbox,
          callback,
          response: responseWithMeta,
          virtualAlias: task.virtualNodeInfo.alias,
        });
        this.cleanupTask(task, { noRecoverVirtualNode: true });
        this.successTaskCount += 1;
        if (isAuthUser()) userAssetsStore.fetchHeartCoin();
      }
    }

    if (this.tasks.size > 0) {
      const interval = this.getInterval();
      console.log("after", interval, "ms execute next check");
      this.timerId = setTimeout(this.check, interval);
    } else {
      if (this.successTaskCount === 0) {
        this.reject(this.mainReason);
      } else {
        this.resolve({
          batchId: this.batchId,
          total: this.total,
          successCount: this.successTaskCount,
        });
      }
    }
  };

  run = (
    postprocess?: (data: {
      cdn: string;
      bbox: BBox;
      virtualAlias?: string;
      callback: () => Promise<void>;
      response: TaskDataType;
    }) => void,
  ) => {
    return new Promise<{ batchId: string } | PromiseLike<{ batchId: string }>>(
      (resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
        this.getStatusFailedCounter = 0;
        this.postprocess = postprocess;
        this.mainReason = undefined;

        this.timerId = setTimeout(() => {
          this.check();
        }, baseInterval * 2);
      },
    );
  };

  getInterval(): number {
    const base = Math.round(baseInterval * (1.0 + Math.random()));
    const executingTasks = this.tasks.filter((task) => !!task.executing);
    const hasWorking = executingTasks.some((task) => task.status === "working");
    if (hasWorking) return base;
    const smallestPending = executingTasks.reduce((smallest, task) => {
      if (smallest === 0) {
        smallest = task.pending;
        return smallest;
      }
      if (task.pending === 0) {
        return smallest;
      }
      return Math.min(task.pending, smallest);
    }, 0);
    const factor = Math.max(1.0, smallestPending - (smallestPending >= 10 ? 8 : 3));

    return factor * base;
  }
}

export async function batchGeneratePolling(
  taskPolling: BatchTaskPolling,
  postprocess?: (data: {
    cdn: string;
    bbox: BBox;
    virtualAlias?: string;
    callback: () => Promise<void>;
    response: TaskDataType;
  }) => void,
): Promise<{ batchId: string } | void> {
  console.log("batchGeneratePolling: ", taskPolling);

  const cleanup = async (e?: any) => {
    if (tasksStore.batchs.includes(taskPolling.batchId)) {
      await tasksStore.removeBatch(taskPolling.batchId);
      await batchTaskPollingPool.remove(taskPolling.batchId);

      Events.getInstance().emit(BatchTaskEvent.FINISH, {
        type: BatchTaskEvent.FINISH,
        data: {
          nextBatchId: tasksStore.batchs[0],
          result: e ?? null,
          type: e?.batchId ? "success" : "failed",
        },
      });
    }

    if (!e?.batchId) {
      throw e;
    }
  };

  return safeCall(() => taskPolling.run(postprocess), {
    success: cleanup,
    failed: cleanup,
  });
}

export async function cancelAllTask(opt?: { cancelThirdPartyTasks: boolean }) {
  const batchIds = tasksStore.batchs;
  // 1. 调接口
  // 2. 接口成功后 清除数据
  return new Promise((resolve, reject) => {
    if (batchIds.length) {
      return cancelBatchTasks(batchIds)
        .then(async () => {
          for (const batchId of batchIds) {
            const batchPolling = batchTaskPollingPool.get(batchId);
            if (batchPolling) {
              await batchPolling.cancelBatchTasks();
            } else {
              tasksStore.cancelBatchTasks(batchId);
            }
          }
          if (opt?.cancelThirdPartyTasks) {
            tasksStore.removeBatchTasks(tasksStore.tasks);
          }
          tasksStore.removeBusyNode(...Array.from(tasksStore.busyNodes));

          resolve({ msg: "Removed batch tasks and third party tasks if necessary" });
        })
        .catch((err) => {
          reject(err);
        });
    } else if (opt?.cancelThirdPartyTasks) {
      tasksStore.removeBatchTasks(tasksStore.tasks);
      tasksStore.removeBusyNode(...Array.from(tasksStore.busyNodes));
      resolve({ msg: "Directly remove all tasks" });
    } else {
      resolve({ msg: "No tasks to remove" });
    }
  });
}
